import { STORAGE_KEYS } from "../config/constants";

/**
 * The only client-side state that is not in the react-query cache.
 *
 * Deliberately thin: the server holds every answer, so this holds the handle on
 * the draft, where to resume, and the prefill chips — which are per-device UI
 * state the server has no opinion about.
 *
 * A draft belongs to the device that created it. Clearing this loses the draft.
 */

/**
 * Edit mode walks the same screens as create but must never touch this
 * storage: a visitor editing a published request would otherwise overwrite the
 * token of an unrelated draft they still had in progress. Latched once by
 * `DraftIdentityProvider` and enforced in the two writers below rather than at
 * each call site, so a new writer cannot forget it.
 */
let isWritable = true;

export const setDraftStorageWritable = (writable: boolean) => {
  isWritable = writable;
};

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari private mode and similar. Losing resume is survivable.
    return null;
  }
};

const write = (key: string, value: string) => {
  if (!isWritable) return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
};

const remove = (key: string) => {
  if (!isWritable) return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    /* no-op */
  }
};

export const getDraftToken = () => read(STORAGE_KEYS.TOKEN);
export const setDraftToken = (token: string) =>
  write(STORAGE_KEYS.TOKEN, token);

export const getLastScreen = () => read(STORAGE_KEYS.LAST_SCREEN);
export const setLastScreen = (screenKey: string) =>
  write(STORAGE_KEYS.LAST_SCREEN, screenKey);

/**
 * Question keys whose prefill chip is gone for good — the user edited the value
 * or dismissed the banner.
 *
 * Sticky by design: returning a field to the assistant's exact value does not
 * bring the chip back.
 */
export const getDismissedPrefills = (): string[] => {
  const raw = read(STORAGE_KEYS.DISMISSED_HINTS);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const dismissPrefill = (questionKey: string) => {
  const dismissed = getDismissedPrefills();
  if (dismissed.includes(questionKey)) return;

  write(
    STORAGE_KEYS.DISMISSED_HINTS,
    JSON.stringify([...dismissed, questionKey])
  );
};

/** Called on publish, on discard, and on a 401 from a stale token. */
export const clearDraftStorage = () => {
  remove(STORAGE_KEYS.TOKEN);
  remove(STORAGE_KEYS.LAST_SCREEN);
  remove(STORAGE_KEYS.DISMISSED_HINTS);
};
